import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, CheckCircle, IdentificationCard } from "@phosphor-icons/react";
import { passwordError, PASSWORD_HINT } from "../lib/password";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => { if (!token) setErr("This reset link is missing its token. Please request a new one."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const pe = passwordError(pw);
    if (pe) return setErr(pe);
    if (pw !== pw2) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Reset failed. The link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-zinc-100" data-testid="reset-root">
      <div className="flex flex-col justify-between p-8 lg:p-14 bg-zinc-950 text-white">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="reset-back">
          <ArrowLeft size={16} /> Back to sign in
        </Link>
        <div>
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-white text-zinc-950 flex items-center justify-center rounded-sm">
              <IdentificationCard weight="fill" size={20} />
            </div>
            <div className="font-display font-black text-xl">SaarOne</div>
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-tight tracking-tight">Set a new password.</h1>
          <p className="text-zinc-400 mt-6 text-lg max-w-md leading-relaxed">Choose something you'll remember. We'll log you in right after.</p>
        </div>
        <div className="text-xs tracking-widest uppercase text-zinc-500">© 2026 SaarOne</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto"/>
              <h2 className="font-display font-bold text-2xl mt-4">Password updated.</h2>
              <p className="text-sm text-zinc-500 mt-2">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-zinc-500">New password</div>
              <h2 className="font-display font-bold text-3xl mt-2">Choose a new one.</h2>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                <div>
                  <Label htmlFor="pw" className="text-xs uppercase tracking-wider font-medium text-zinc-600">New password</Label>
                  <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={10}
                    className="mt-2 h-11 bg-white border-zinc-300 rounded-md" data-testid="reset-pw-input"/>
                  <p className="mt-1.5 text-[11px] text-zinc-500" data-testid="reset-pw-hint">{PASSWORD_HINT}</p>
                </div>
                <div>
                  <Label htmlFor="pw2" className="text-xs uppercase tracking-wider font-medium text-zinc-600">Confirm password</Label>
                  <Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={10}
                    className="mt-2 h-11 bg-white border-zinc-300 rounded-md" data-testid="reset-pw2-input"/>
                </div>
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="reset-error">{err}</div>}
                <Button type="submit" disabled={busy || !token} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 rounded-md" data-testid="reset-submit">
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
